import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Chart from 'chart.js/auto';

// Helper function to convert table HTML to CSV
const tableToCSV = (tableElement: HTMLTableElement) => {
  const rows = tableElement.querySelectorAll('tr');
  const csvContent = [];

  rows.forEach(row => {
    const cells = row.querySelectorAll('th, td');
    const rowData = Array.from(cells).map(cell => {
      const content = (cell as HTMLElement).getAttribute('data-raw-value') ||
                     (cell as HTMLElement).textContent?.replace(/"/g, '""') || '';
      return `"${content}"`;
    });
    csvContent.push(rowData.join(','));
  });

  return csvContent.join('\n');
};

// Helper function to handle CSV download
const downloadTableAsCSV = (tableElement: HTMLTableElement, fileName = 'table-data.csv') => {
  const csv = tableToCSV(tableElement);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface DiMarkdownProps {
  children: string;
}

const DiMarkdown: React.FC<DiMarkdownProps> = ({ children }) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const chartInstancesRef = useRef<Chart[]>([]);

  useEffect(() => {
    return () => {
      chartInstancesRef.current.forEach(chart => chart.destroy());
      chartInstancesRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      const tables = tableRef.current.querySelectorAll('table');
      tables.forEach((table, index) => {
        // Process ticker columns
        const headers = table.querySelectorAll('th');
        const tickerColumnIndexes = Array.from(headers).reduce((acc: number[], header, index) => {
          if (header.textContent?.toLowerCase().trim() === 'ticker') {
            acc.push(index);
          }
          return acc;
        }, []);

        if (tickerColumnIndexes.length > 0) {
          const rows = table.querySelectorAll('tbody tr');
          rows.forEach(row => {
            const cells = row.querySelectorAll('td');
            tickerColumnIndexes.forEach(colIndex => {
              const cell = cells[colIndex];
              if (cell) {
                const ticker = cell.textContent?.trim();
                if (ticker) {
                  const link = document.createElement('a');
                  link.href = `https://decodeinvesting.com/ticker/${ticker}`;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.textContent = ticker;
                  // Store original value for CSV export
                  cell.setAttribute('data-raw-value', ticker);
                  cell.textContent = '';
                  cell.appendChild(link);
                }
              }
            });
          });
        }

        // Create main wrapper div
        const wrapper = document.createElement('div');
        wrapper.style.marginBottom = '16px';

        // Create responsive wrapper
        const responsiveWrapper = document.createElement('div');
        responsiveWrapper.className = 'table-responsive-wrapper';

        // Move table into responsive wrapper
        table.parentNode?.insertBefore(wrapper, table);
        responsiveWrapper.appendChild(table);
        wrapper.appendChild(responsiveWrapper);

        // Add download button
        const button = document.createElement('button');
        button.className = 'table-download-btn';
        button.innerHTML = `
          <svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" aria-hidden="true" viewBox="0 0 24 24">
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path>
          </svg>
          Download CSV
        `;
        button.onclick = () => downloadTableAsCSV(table, `table-${index + 1}.csv`);
        wrapper.appendChild(button);
      });

      // Update chart processing
      const codeBlocks = tableRef.current.querySelectorAll('code');
      codeBlocks.forEach((codeBlock) => {
        try {
          if (codeBlock.className.includes('language-chart')) {
            const chartData = JSON.parse(codeBlock.textContent || '');
            const canvas = document.createElement('canvas');
            codeBlock.parentElement?.replaceWith(canvas);

            // Create and store chart instance
            const chartInstance = new Chart(canvas, {
              type: chartData.type || 'line',
              data: chartData.data,
              options: chartData.options || {}
            });
            chartInstancesRef.current.push(chartInstance);
          }
        } catch (error) {
          console.error('Failed to parse chart data:', error);
        }
      });
    }
  }, [children]);

  return (
    <ErrorBoundary fallback={<div>Error rendering markdown content</div>}>
      <style>
        {`
          /* Light theme */
          :root {
            --color-canvas-default: #ffffff;
            --color-canvas-subtle: #f6f8fa;
            --color-border-default: #d0d7de;
            --color-border-muted: #eaecef;
            --color-stripe: #f8f9fa;
            --color-header-bg: #f1f3f5;
          }

          table {
            border-spacing: 0;
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 8px;
            font-size: 13px;
            border: 1px solid var(--color-border-muted);
          }

          tr {
            background-color: var(--color-canvas-default);
            border-bottom: 1px solid var(--color-border-muted);
          }

          tbody tr:nth-child(even) {
            background-color: var(--color-stripe);
          }

          thead tr {
            background-color: var(--color-header-bg) !important;
          }

          td, th {
            padding: 6px 12px;
            text-align: left;
            border-right: 1px solid var(--color-border-muted);
          }

          td:last-child, th:last-child {
            border-right: none;
          }

          th {
            font-weight: 500;
            color: #333;
          }

          td {
            color: #555;
          }

          .table-responsive-wrapper {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin-bottom: 8px;
          }

          table {
            width: 100%;
            min-width: 650px;
            margin-bottom: 0;
          }

          .chat-message .table-responsive-wrapper {
            padding: 0 1px;
          }

          .table-download-btn {
            background-color: #fff;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 6px 12px;
            margin-top: 4px;
            margin-bottom: 4px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            color: #666;
            transition: all 0.2s ease;
          }

          .table-download-btn:hover {
            background-color: #f8f9fa;
            border-color: #ced4da;
            color: #333;
          }

          .table-download-btn svg {
            width: 16px;
            height: 16px;
          }
        `}
      </style>
      <div ref={tableRef}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
          }}
        >
          {children}
        </ReactMarkdown>
      </div>
    </ErrorBoundary>
  );
};

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default DiMarkdown;
